import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { BadgeCheck, ShieldCheck, ClipboardList, Receipt, History } from 'lucide-react';

export const POLL_MS = 15000;

export const fmtEur = (n) => `€${(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const fmtDate = (iso) => !iso ? '—' : new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
export const fmtDateTime = (iso) => !iso ? '—' : new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

export const EVENT_META = {
    authorized: { icon: ShieldCheck, color: '#10b981' },
    registered: { icon: ClipboardList, color: '#06b6d4' },
    payment_registered: { icon: Receipt, color: '#f59e0b' },
    updated: { icon: History, color: '#94a3b8' },
    incorporated: { icon: ClipboardList, color: '#06b6d4' },
    request_withdrawal: { icon: History, color: '#1973B8' },
    approve: { icon: ShieldCheck, color: '#10b981' },
    confirm_disbursement: { icon: Receipt, color: '#10b981' },
    fulfill_commitment: { icon: BadgeCheck, color: '#10b981' },
    send_review: { icon: History, color: '#f59e0b' },
    commitment_overdue: { icon: History, color: '#ef4444' },
    reject: { icon: History, color: '#ef4444' },
    exclude: { icon: History, color: '#64748b' },
    receipt_uploaded: { icon: Receipt, color: '#06b6d4' },
    receipt_removed: { icon: History, color: '#94a3b8' },
};


// ─── Progress ────────────────────────────────────────────────────
export const ProgressIndicator = ({ count, total, pct }) => (
    <Card className="p-4 sm:p-5 bg-slate-900/60 border border-slate-800" data-testid="authorized-tx-progress">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
            <div>
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#7CB1E5] font-bold">Indicador de progreso</p>
                <p className="text-white text-lg sm:text-xl font-bold mt-0.5 tabular-nums" data-testid="authorized-tx-progress-label">
                    {count} <span className="text-slate-500 font-normal">/ {total}</span> <span className="text-[13px] text-slate-300 font-semibold">usuarios incorporados</span>
                </p>
            </div>
            <p className="text-emerald-300 text-2xl font-bold font-mono tabular-nums" data-testid="authorized-tx-progress-pct">{pct}%</p>
        </div>
        <div className="h-3 rounded-full bg-slate-800 overflow-hidden ring-1 ring-slate-700/60" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                className="h-full rounded-full bg-gradient-to-r from-[#1973B8] via-cyan-400 to-emerald-400 shadow-[0_0_14px_rgba(16,185,129,0.45)]"
                data-testid="authorized-tx-progress-bar"
            />
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-slate-500 font-mono">
            <span>0</span><span>{Math.round(total / 2)}</span><span>{total}</span>
        </div>
    </Card>
);


// ─── Timeline ────────────────────────────────────────────────────
export const Timeline = ({ events, updatedAt }) => {
    const list = [...(events || []), { type: 'updated', label: 'Última actualización', at: updatedAt, note: 'Registro al día' }];
    return (
        <ol className="relative border-l-2 border-slate-800 ml-2 space-y-3.5" data-testid="authorized-tx-timeline">
            {list.map((ev, i) => {
                const meta = EVENT_META[ev.type] || EVENT_META.updated;
                const Icon = meta.icon;
                return (
                    <li key={`${ev.type}-${i}`} className="ml-4 pl-2" data-testid={`authorized-tx-event-${ev.type}`}>
                        <span className="absolute -left-3 w-6 h-6 rounded-full flex items-center justify-center ring-4 ring-slate-950" style={{ background: meta.color + '25' }}>
                            <Icon className="w-3 h-3" style={{ color: meta.color }} />
                        </span>
                        <div className="flex items-center justify-between gap-2">
                            <p className="font-bold text-[12.5px]" style={{ color: meta.color }}>{ev.label}</p>
                            <span className="text-slate-400 text-[10.5px] font-mono whitespace-nowrap">{fmtDateTime(ev.at)}</span>
                        </div>
                        {ev.note && <p className="text-slate-400 text-[11px] mt-0.5">{ev.note}</p>}
                    </li>
                );
            })}
        </ol>
    );
};


// ─── Ficha individual ────────────────────────────────────────────
export const Field = ({ label, value, mono, testid }) => (
    <div className="min-w-0">
        <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
        <p className={`text-white text-[12.5px] mt-0.5 break-all ${mono ? 'font-mono' : 'font-semibold'}`} data-testid={testid}>{value ?? '—'}</p>
    </div>
);

export const StatusBadge = ({ label }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] font-bold uppercase tracking-[0.06em] whitespace-nowrap">
        <BadgeCheck className="w-3 h-3" /> {label}
    </span>
);


